import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from '../config';
import Sidebar from "./Dashboard/SideBar.js";
import Header from "./Dashboard/Header.js";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import "jspdf-autotable";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import {
  PictureAsPdf,
  TableView,
  MoreVert,
  Search as SearchIcon,
  ArrowDownward,
  ArrowUpward,
  Sort,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";


const SalesmanReport = () => {
  // ✅ Always today
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'ascending' });

  const selectedCompany = JSON.parse(localStorage.getItem("selectedCompany"));
  const userId = localStorage.getItem("userId");

  const theme = useTheme();

  // ✅ Load today’s data automatically
  useEffect(() => {
    if (fromDate && toDate) {
      fetchSalesmanReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const fetchSalesmanReport = async () => {
    if (!selectedCompany || !userId) {
      setError("Missing company or user info in local storage");
      return;
    }

    const { CompCode } = selectedCompany;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_BASE_URL}/api/salesman-report`,
        {
          params: { CompCode, fromDate, toDate },
          headers: { userid: userId },
        }
      );
      setData(response.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load data. Check network or API URL.");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];
    const q = searchQuery.toLowerCase();

    if (q) {
      filtered = filtered.filter(
        (r) =>
          r.Name?.toLowerCase().includes(q) ||
          r.AMT?.toString().includes(q) ||
          r.Qty?.toString().includes(q)
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue === bValue) return 0;
        const comparison = (aValue < bValue) ? -1 : 1;
        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [data, searchQuery, sortConfig]);

  const totalAmount = useMemo(() => {
    return filteredData.reduce((sum, r) => sum + (r.AMT || 0), 0);
  }, [filteredData]);

  const totalQty = useMemo(() => {
    return filteredData.reduce((sum, r) => sum + (r.Qty || 0), 0);
  }, [filteredData]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const generatePDF = () => {
    const doc = new jsPDF("landscape");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Salesman Report", doc.internal.pageSize.width / 2, 20, {
      align: "center",
    });

    const headers = ["#", "Name", "Amount", "Qty"];
    const rows = data.map((r, i) => [
      i + 1,
      r.Name || "N/A",
      r.AMT?.toFixed(2),
      r.Qty?.toFixed(2),
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 30,
      styles: { fontSize: 9, halign: "center" },
      headStyles: { fillColor: [33, 150, 243] },
    });

    doc.save("salesman_report.pdf");
  };

  const generateExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Salesman Report");
    XLSX.writeFile(wb, "salesman_report.xlsx");
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth <= 768);

  const handleSidebarToggle = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
  };

  return (
    <Box className="flex h-screen overflow-hidden bg-slate-50 font-sans">
      <Sidebar onToggle={handleSidebarToggle} />
      <Box
        component="main"
        className={`flex-1 flex flex-col h-full transition-all duration-300 ${isSidebarCollapsed ? 'ml-0' : 'ml-0 md:ml-[260px]'}`}
      >
        <Header />
        <Box className="flex-1 overflow-y-auto p-4 md:p-6 scroll-smooth bg-slate-50/50">
          <Box className="mb-4 md:mb-6">
            <Box className="flex items-center justify-center relative mb-4 md:mb-6">
              <Box className="text-center">
                <Typography variant="h5" component="h1" className="font-bold text-gray-800 text-lg md:text-3xl tracking-tight">
                  Salesman Report
                </Typography>
              </Box>
            </Box>

            <Box className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white p-2 md:p-3 rounded-xl md:rounded-2xl shadow-sm border border-gray-200">
              <Box className="flex flex-col sm:flex-row items-center gap-3 w-full lg:max-w-3xl">
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full sm:w-50 bg-gray-50/50 rounded-lg"
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full sm:w-50 bg-gray-50/50 rounded-lg"
                />
                <TextField
                  variant="outlined"
                  placeholder="Search Salesman..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  className="w-full bg-gray-50/50 rounded-lg"
                />
              </Box>

              <Box className="flex flex-row items-center justify-between lg:justify-end gap-4 w-full lg:w-auto">
                <Box className="flex gap-4">
                  <Box className="text-left lg:text-right">
                    <Typography variant="caption" className="text-gray-400 uppercase font-bold tracking-wider text-[10px]">
                      Total Amount
                    </Typography>
                    <Typography variant="subtitle1" className="font-bold leading-tight text-blue-600">
                      Rs {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box className="text-left lg:text-right">
                    <Typography variant="caption" className="text-gray-400 uppercase font-bold tracking-wider text-[10px]">
                      Total Qty
                    </Typography>
                    <Typography variant="subtitle1" className="font-bold leading-tight text-green-600">
                      {totalQty.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleExportClick}
                    endIcon={<ArrowDownward />}
                    size="small"
                    className="rounded-lg px-4 py-1.5"
                  >
                    Export
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleExportClose}
                    PaperProps={{
                      className: "mt-1 shadow-xl border border-gray-100 rounded-xl"
                    }}
                  >
                    <MenuItem onClick={() => { generatePDF(); handleExportClose(); }} className="gap-3 py-2 px-4">
                      <PictureAsPdf className="text-red-500" />
                      <Typography variant="body2" className="font-medium">PDF</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => { generateExcel(); handleExportClose(); }} className="gap-3 py-2 px-4">
                      <TableView className="text-green-600" />
                      <Typography variant="body2" className="font-medium">Excel</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>
          </Box>

          {loading && (
            <Box className="flex flex-col items-center justify-center py-20">
              <CircularProgress size={60} thickness={4} />
              <Typography className="mt-4 text-slate-500 font-medium">Loading report data...</Typography>
            </Box>
          )}

          {error && (
            <Box className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg mb-6">
              <Typography className="font-medium">Error</Typography>
              <Typography>{error}</Typography>
            </Box>
          )}

          {!loading && !error && filteredData.length > 0 && (
            <Paper elevation={1} className="overflow-hidden w-full rounded-xl border border-gray-300">
              <Box className="overflow-x-auto">
                <Box
                  className="bg-blue-50 p-2 border-b border-gray-300 grid items-center"
                  style={{ gridTemplateColumns: '80px 1fr 150px 120px', width: '100%', minWidth: '600px' }}
                >
                  <Box className="text-blue-800 font-bold text-xs md:text-sm px-2">#</Box>
                  <Box
                    className="flex items-center cursor-pointer text-blue-800 font-bold text-xs md:text-sm hover:text-blue-600 transition-colors"
                    onClick={() => handleSort('Name')}
                  >
                    Salesman
                    {sortConfig.key === 'Name' ? (
                      sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="inherit" className="ml-1" /> : <ArrowDownward fontSize="inherit" className="ml-1" />
                    ) : <Sort fontSize="inherit" className="ml-1 opacity-30" />}
                  </Box>
                  <Box
                    className="flex items-center justify-end cursor-pointer text-blue-800 font-bold text-xs md:text-sm hover:text-blue-600 transition-colors pr-4"
                    onClick={() => handleSort('AMT')}
                  >
                    Amount
                    {sortConfig.key === 'AMT' ? (
                      sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="inherit" className="ml-1" /> : <ArrowDownward fontSize="inherit" className="ml-1" />
                    ) : <Sort fontSize="inherit" className="ml-1 opacity-30" />}
                  </Box>
                  <Box
                    className="flex items-center justify-end cursor-pointer text-blue-800 font-bold text-xs md:text-sm hover:text-blue-600 transition-colors pr-2"
                    onClick={() => handleSort('Qty')}
                  >
                    Quantity
                    {sortConfig.key === 'Qty' ? (
                      sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="inherit" className="ml-1" /> : <ArrowDownward fontSize="inherit" className="ml-1" />
                    ) : <Sort fontSize="inherit" className="ml-1 opacity-30" />}
                  </Box>
                </Box>

                <Box style={{ width: '100%', minWidth: '600px' }}>
                  {paginatedData.map((row, index) => (
                    <Box
                      key={index}
                      className="border-b border-gray-300 last:border-b-0 p-2 grid items-center hover:bg-gray-50/50 transition-colors"
                      style={{ gridTemplateColumns: '80px 1fr 150px 120px' }}
                    >
                      <Typography variant="body2" className="text-gray-500 px-2">
                        {(page - 1) * rowsPerPage + index + 1}
                      </Typography>
                      <Typography variant="body2" className="font-semibold text-gray-700 truncate">
                        {row.Name || "-"}
                      </Typography>
                      <Typography variant="body2" className="font-bold text-blue-600 text-right pr-4">
                        {row.AMT?.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                        }) || "₹0.00"}
                      </Typography>
                      <Typography variant="body2" className="text-gray-600 text-right pr-2">
                        {row.Qty?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          )}

          {!loading && !error && filteredData.length === 0 && (
            <Box className="text-center py-20 bg-white rounded-xl border border-slate-200">
              <Typography className="text-slate-400">No data available for the selected criteria.</Typography>
            </Box>
          )}

          <Box className="mt-8 flex items-center justify-center gap-4">
            <Button
              variant="text"
              size="small"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="text-blue-600 font-bold"
            >
              Start
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              &#8592;
            </Button>
            <Typography variant="body2" className="text-slate-600 font-medium">
              Page {page} of {totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
            >
              &#8594;
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="text-blue-600 font-bold"
            >
              End
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SalesmanReport;
